import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import AnnouncementsTab from './AnnouncementsTab';
import { formatLocal } from '../utils/timezones';
import api from '../api';

const NewsletterDashboard = ({ user, hoaData }) => {
  const [activeTab, setActiveTab] = useState('compose');
  const [canCreate, setCanCreate] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    checkPermissions();
    loadSettings();
  }, []);

  const checkPermissions = async () => {
    try {
      const response = await fetch(`/api/newsletter/settings/${user.hoa_id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check if user can create newsletters
        const boardRoles = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'];
        const canCreateByRole = boardRoles.includes(user.role);
        const canCreateByIndividual = data.allowed_individuals?.includes(user.id);
        
        setCanCreate(canCreateByRole || canCreateByIndividual || !data.board_only_create);
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const loadSettings = async () => {
    try {
      console.log('Loading newsletter settings for HOA:', user.hoa_id);
      const response = await fetch(`/api/newsletter/settings/${user.hoa_id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Newsletter settings loaded:', data);
        setSettings(data);
      } else {
        console.error('Failed to load settings, response:', response.status);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const brandColor = hoaData?.branding?.primary_color || hoaData?.branding?.primaryColor || '#5A2D82';
  const logoUrl = hoaData?.branding?.logo_url || (hoaData?.branding?.logo_base64 ? 
    `data:${hoaData.branding.logo_content_type};base64,${hoaData.branding.logo_base64}` : 
    null);

  return (
    <div className="p-6">
      {/* Header with HOA branding */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-2">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt="HOA Logo" 
              className="h-12 w-12 object-contain"
            />
          )}
          <h1 
            className="text-3xl font-bold"
            style={{ color: brandColor }}
          >
            Newsletter
          </h1>
        </div>
        <p className="text-gray-600">
          Create and manage community newsletters
        </p>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Custom styles for scrollable tabs */}
        <style jsx>{`
          .scrollable-tabs {
            display: flex;
            overflow-x: auto;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .scrollable-tabs::-webkit-scrollbar {
            display: none;
          }
          .tab-button {
            flex: none;
            white-space: nowrap;
            min-width: max-content;
            padding: 8px 16px;
          }
          @media (min-width: 768px) {
            .scrollable-tabs {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              overflow: visible;
            }
          }
        `}</style>
        
        <TabsList className="scrollable-tabs md:grid md:grid-cols-5 md:w-full">
          <TabsTrigger value="compose" className="tab-button">Compose</TabsTrigger>
          <TabsTrigger value="current-issue" className="tab-button">Current Issue</TabsTrigger>
          <TabsTrigger value="announcements" className="tab-button">Announcements</TabsTrigger>
          <TabsTrigger value="vault" className="tab-button">The Vault</TabsTrigger>
          <TabsTrigger value="settings" className="tab-button">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-6">
          <NewsletterComposer 
            user={user} 
            hoaData={hoaData}
            canCreate={canCreate} 
            brandColor={brandColor}
            logoUrl={logoUrl}
          />
        </TabsContent>

        <TabsContent value="current-issue" className="mt-6">
          <CurrentIssueTab 
            user={user} 
            hoaData={hoaData}
            settings={settings}
          />
        </TabsContent>

        <TabsContent value="announcements" className="mt-6">
          <AnnouncementsTab 
            user={user} 
            hoaData={hoaData}
          />
        </TabsContent>

        <TabsContent value="vault" className="mt-6">
          <NewsletterArchive 
            user={user} 
            hoaData={hoaData}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <NewsletterSettings 
            user={user} 
            hoaData={hoaData}
            settings={settings}
            onSettingsUpdate={loadSettings}
            canEdit={canCreate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Newsletter Composer Component
const NewsletterComposer = ({ user, hoaData, canCreate, brandColor, logoUrl }) => {
  const [newsletters, setNewsletters] = useState([]);
  const [currentNewsletter, setCurrentNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [publishedNewsletters, setPublishedNewsletters] = useState([]);
  const [selectedMeetingIds, setSelectedMeetingIds] = useState({}); // Track selected meeting per section

  useEffect(() => {
    loadCurrentNewsletters();
    loadNewsletterSettings();
    loadUpcomingMeetings();
    loadPublishedNewsletters();
  }, []);

  const loadCurrentNewsletters = async () => {
    try {
      const response = await fetch(`/api/newsletter?hoa_id=${user.hoa_id}&status=draft`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewsletters(data.newsletters);
        
        // Set the most recent draft as current
        if (data.newsletters.length > 0) {
          loadNewsletter(data.newsletters[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNewsletterSettings = async () => {
    try {
      const response = await fetch(`/api/newsletter/settings/${user.hoa_id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading newsletter settings:', error);
    }
  };

  const loadUpcomingMeetings = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const futureDate = new Date();
      futureDate.setMonth(futureDate.getMonth() + 3);
      const endDate = futureDate.toISOString().split('T')[0];
      
      console.log('Loading meetings for HOA:', user.hoa_id, 'from', today, 'to', endDate);
      
      const response = await fetch(`/api/calendar/events?hoa_code=${user.hoa_id}&start_date=${today}&end_date=${endDate}&event_type=board_meeting&limit=5`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded meetings full response:', data); // Debug log
        console.log('Meetings events array:', data.events); // Debug log
        if (data.events && data.events.length > 0) {
          console.log('First meeting structure:', data.events[0]);
          console.log('First meeting ID field:', data.events[0].id);
          console.log('First meeting _id field:', data.events[0]._id);
        }
        setUpcomingMeetings(data.events || []);
      } else {
        const errorText = await response.text();
        console.error('Failed to load meetings:', response.status, response.statusText, errorText);
        if (response.status === 403) {
          console.error('Permission denied - user may not have read access to calendar events');
        }
        setUpcomingMeetings([]); // Set empty array on error
      }
    } catch (error) {
      console.error('Error loading upcoming meetings:', error);
      setUpcomingMeetings([]); // Set empty array on error
    }
  };

  const loadPublishedNewsletters = async () => {
    try {
      const response = await fetch(`/api/newsletter?hoa_id=${user.hoa_id}&status=published&limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPublishedNewsletters(data.newsletters || []);
      }
    } catch (error) {
      console.error('Error loading published newsletters:', error);
    }
  };

  const loadNewsletter = async (id) => {
    try {
      const response = await fetch(`/api/newsletter/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentNewsletter(data);
      }
    } catch (error) {
      console.error('Error loading newsletter:', error);
    }
  };

  const createNewNewsletter = async () => {
    if (!canCreate) return;
    
    try {
      setSaving(true);
      const response = await fetch(`/api/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: JSON.stringify({
          hoa_id: user.hoa_id,
          title: `${hoaData.name} Newsletter – ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        await loadNewsletter(data.id);
        await loadCurrentNewsletters();
      }
    } catch (error) {
      console.error('Error creating newsletter:', error);
    } finally {
      setSaving(false);
    }
  };

  const saveNewsletter = async () => {
    if (!currentNewsletter || !canCreate) return;
    
    try {
      setSaving(true);
      const response = await fetch(`/api/newsletter/${currentNewsletter.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: JSON.stringify({
          title: currentNewsletter.title,
          header_image_id: currentNewsletter.header_image_id,
          header_display_mode: currentNewsletter.header_display_mode || 'landscape',
          sections: currentNewsletter.sections.map(section => ({
            ...section,
            columns: section.columns || 1,
            header_image_id: section.header_image_id || null,
            column_content: section.column_content || [section.html || '']
          })),
          issue_date: currentNewsletter.issue_date
        })
      });
      
      if (response.ok) {
        alert('Newsletter saved successfully!');
      }
    } catch (error) {
      console.error('Error saving newsletter:', error);
      alert('Error saving newsletter');
    } finally {
      setSaving(false);
    }
  };

  const publishNewsletter = async () => {
    if (!currentNewsletter || !canCreate) return;
    
    if (!confirm('Are you sure you want to publish this newsletter? This action cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      // Save first
      await saveNewsletter();
      
      // Then publish
      const response = await fetch(`/api/newsletter/${currentNewsletter.id}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        alert('Newsletter published successfully!');
        await loadCurrentNewsletters();
        setCurrentNewsletter(null);
      }
    } catch (error) {
      console.error('Error publishing newsletter:', error);
      alert('Error publishing newsletter');
    } finally {
      setSaving(false);
    }
  };

  const updateNewsletterField = (field, value) => {
    setCurrentNewsletter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateSection = (sectionIndex, field, value) => {
    setCurrentNewsletter(prev => ({
      ...prev,
      sections: prev.sections.map((section, index) => 
        index === sectionIndex ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateSectionColumnContent = (sectionIndex, columnIndex, content) => {
    setCurrentNewsletter(prev => ({
      ...prev,
      sections: prev.sections.map((section, index) => {
        if (index === sectionIndex) {
          const columnContent = [...(section.column_content || [])];
          columnContent[columnIndex] = content;
          
          // If single column, also update the legacy html field for backward compatibility
          const updatedSection = {
            ...section,
            column_content: columnContent
          };
          
          if (section.columns === 1 || !section.columns) {
            updatedSection.html = content;
          }
          
          return updatedSection;
        }
        return section;
      })
    }));
  };

  const copyFromSettings = (sectionIndex) => {
    if (!settings || !currentNewsletter) return;
    
    const currentSection = currentNewsletter.sections[sectionIndex];
    const defaultSection = settings.default_sections?.find(ds => ds.key === currentSection.key);
    
    if (defaultSection) {
      // Only copy to the first column (index 0)
      updateSectionColumnContent(sectionIndex, 0, defaultSection.html);
    }
  };

  const uploadSectionImage = async (file, sectionIndex) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/newsletter/upload-header', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageHtml = `<img src="/api/newsletter/asset/${data.id}" alt="Section Image" style="max-width: 300px; height: auto; margin: 10px 0; border-radius: 5px;" />`;
        
        // Append image to current section content
        const currentSection = currentNewsletter.sections[sectionIndex];
        const newContent = currentSection.html + '\n' + imageHtml;
        updateSection(sectionIndex, 'html', newContent);
      }
    } catch (error) {
      console.error('Error uploading section image:', error);
    }
  };

  const uploadSectionHeaderImage = async (file, sectionIndex) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/newsletter/upload-header', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        updateSection(sectionIndex, 'header_image_id', data.id);
      }
    } catch (error) {
      console.error('Error uploading section header image:', error);
    }
  };

  const selectBoardMeeting = (sectionIndex, meetingId, columnIndex = 0) => {
    console.log('selectBoardMeeting called with sectionIndex:', sectionIndex, 'meetingId:', meetingId, 'columnIndex:', columnIndex);
    // Store the selected meeting ID for this section and column
    const key = columnIndex !== undefined ? `${sectionIndex}-${columnIndex}` : sectionIndex;
    setSelectedMeetingIds(prev => {
      const newIds = {
        ...prev,
        [key]: meetingId
      };
      console.log('Updated selectedMeetingIds:', newIds);
      return newIds;
    });
  };

  const addMeetingDetails = (sectionIndex, columnIndex = 0) => {
    console.log('addMeetingDetails called with sectionIndex:', sectionIndex, 'columnIndex:', columnIndex);
    console.log('selectedMeetingIds:', selectedMeetingIds);
    console.log('upcomingMeetings:', upcomingMeetings);
    
    const key = columnIndex !== undefined ? `${sectionIndex}-${columnIndex}` : sectionIndex;
    const meetingId = selectedMeetingIds[key];
    console.log('meetingId for section:', meetingId);
    
    if (!meetingId) {
      console.log('No meetingId found, returning');
      alert('Please select a meeting from the dropdown first.');
      return;
    }
    
    // Use same ID prioritization as dropdown
    const meeting = upcomingMeetings.find(m => {
      const mId = m.id || m._id || m.event_id;
      return mId === meetingId || String(mId) === String(meetingId);
    });
    
    console.log('Found meeting:', meeting);
    
    if (!meeting) {
      console.log('No meeting found in upcomingMeetings after comprehensive search');
      alert('Selected meeting not found. Please refresh and try again.');
      return;
    }
    
    addMeetingDetailsWithMeeting(sectionIndex, meeting, columnIndex);
  };

  const addMeetingDetailsWithMeeting = (sectionIndex, meeting, columnIndex = 0) => {
    console.log('Adding meeting details:', meeting, 'for column:', columnIndex);
    
    // Get current section content - MUST BE BEFORE using currentColumns
    const currentSection = currentNewsletter.sections[sectionIndex];
    const currentColumns = currentSection.columns || 1;
    
    // Handle different possible date field names and formats
    let meetingDate, meetingTime;
    const dateField = meeting.start_datetime || meeting.start_date || meeting.date || meeting.event_date;
    
    if (dateField) {
      try {
        const date = new Date(dateField);
        if (!isNaN(date.getTime())) {
          meetingDate = date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          
          meetingTime = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        } else {
          meetingDate = 'Date TBD';
          meetingTime = 'Time TBD';
        }
      } catch (error) {
        console.error('Date parsing error:', error);
        meetingDate = 'Date TBD';
        meetingTime = 'Time TBD';
      }
    } else {
      meetingDate = 'Date TBD';
      meetingTime = 'Time TBD';
    }
    
    const meetingHtml = `
      <p><strong>Board Meeting${currentColumns > 1 ? ` ${columnIndex + 1}` : ''}</strong></p>
      <p><strong>Title:</strong> ${meeting.title || meeting.name || 'Board Meeting'}</p>
      <p><strong>Date:</strong> ${meetingDate}</p>
      <p><strong>Time:</strong> ${meetingTime}</p>
      ${meeting.location ? `<p><strong>Location:</strong> ${meeting.location}</p>` : ''}
      ${meeting.video_url ? `<p><strong>Zoom:</strong> <a href="${meeting.video_url}" target="_blank">Join link</a></p>` : ''}
      ${meeting.description ? `<p><strong>Agenda:</strong> ${meeting.description}</p>` : ''}
      <p>All homeowners are welcome to attend.</p>
    `;
    
    console.log('Generated meetingHtml:', meetingHtml);
    
    if (currentColumns > 1) {
      // For multi-column layout, update the specific column
      updateSectionColumnContent(sectionIndex, columnIndex, meetingHtml);
    } else {
      // For single column, update first column
      updateSectionColumnContent(sectionIndex, 0, meetingHtml);
    }
    
    // Visual feedback
    alert(`Board meeting details added to newsletter!\n\nMeeting: ${meeting.title || 'Board Meeting'}\nDate: ${meetingDate}\nTime: ${meetingTime}`);
  };

  const rolloverFromPrevious = async (newsletterId) => {
    if (!newsletterId) return;
    
    try {
      setSaving(true);
      
      // Use the new rollover endpoint
      const createResponse = await fetch(`/api/newsletter/${newsletterId}/rollover`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (createResponse.ok) {
        const newData = await createResponse.json();
        await loadNewsletter(newData.id);
        await loadCurrentNewsletters();
        
        // Show warning about dates and links
        alert('Newsletter created from previous template!\n\n⚠️ IMPORTANT REMINDERS:\n• Update all dates to current month\n• Check and update any links or references\n• Review all content for accuracy\n• Update meeting dates and event information');
      } else {
        const errorData = await createResponse.json();
        alert('Error creating newsletter from template: ' + (errorData.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error rolling over newsletter:', error);
      alert('Error creating newsletter from template');
    } finally {
      setSaving(false);
    }
  };

  const deleteCurrentNewsletter = async () => {
    if (!currentNewsletter || !canCreate) return;
    
    if (!confirm(`Are you sure you want to delete "${currentNewsletter.title}"?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await fetch(`/api/newsletter/${currentNewsletter.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        alert('Newsletter deleted successfully!');
        setCurrentNewsletter(null);
        await loadCurrentNewsletters();
      } else {
        const errorData = await response.json();
        alert('Error deleting newsletter: ' + (errorData.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting newsletter:', error);
      alert('Error deleting newsletter');
    } finally {
      setSaving(false);
    }
  };

  const uploadHeaderImage = async (file) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`/api/newsletter/upload-header`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        updateNewsletterField('header_image_id', data.id);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  if (!canCreate) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">You don't have permission to create newsletters.</p>
        <p className="text-sm text-gray-500 mt-2">Contact your board members for access.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8 min-h-0">
      {/* Editor */}
      <div className="space-y-6 min-w-0">
        {/* Newsletter Selection */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-col gap-4 mb-4">
            <h3 className="text-lg font-semibold">Current Drafts</h3>
            <div className="flex flex-col lg:flex-row gap-3">
              {/* Rollover from Previous Newsletter */}
              {publishedNewsletters.length > 0 && (
                <div className="flex-1 min-w-0">
                  <select
                    onChange={(e) => e.target.value && rolloverFromPrevious(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    defaultValue=""
                  >
                    <option value="">📄 Use Previous as Template</option>
                    {publishedNewsletters.slice(0, 5).map((newsletter, index) => {
                      const timezone = hoaData?.timezone || 'America/Chicago';
                      const dateToFormat = newsletter.issue_date || newsletter.created_at || newsletter.published_at;
                      const formattedDate = dateToFormat 
                        ? formatLocal(dateToFormat, timezone, 'MMM d, yyyy')
                        : 'No date';
                      
                      return (
                        <option key={`published-${newsletter.id}-${index}`} value={newsletter.id}>
                          {newsletter.title} ({formattedDate})
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              
              <button
                onClick={createNewNewsletter}
                disabled={saving}
                className="px-6 py-2 rounded text-white font-medium whitespace-nowrap"
                style={{ backgroundColor: brandColor }}
              >
                New Newsletter
              </button>
            </div>
          </div>
          
          {/* Warning about rollover feature */}
          {publishedNewsletters.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>📄 Template Feature:</strong> Use "Previous as Template" to copy content from published newsletters. 
                <strong> Remember to update dates, links, and all content!</strong>
              </p>
            </div>
          )}
          
          {newsletters.length === 0 ? (
            <p className="text-gray-500">No draft newsletters. Create one to get started.</p>
          ) : (
            <div className="space-y-2">
              {newsletters.map((newsletter, index) => (
                <div
                  key={`newsletter-${newsletter.id}-${index}`}
                  className={`p-3 rounded border cursor-pointer ${
                    currentNewsletter?.id === newsletter.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => loadNewsletter(newsletter.id)}
                >
                  <div className="font-medium">{newsletter.title}</div>
                  <div className="text-sm text-gray-500">
                    Created: {new Date(newsletter.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Newsletter Editor */}
        {currentNewsletter && (
          <div className="bg-white rounded-lg border p-6">
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Newsletter Title
                </label>
                <input
                  type="text"
                  value={currentNewsletter.title}
                  onChange={(e) => updateNewsletterField('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Header Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Header Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadHeaderImage(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                
                {/* Header Display Mode Toggle */}
                {currentNewsletter.header_image_id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Header Display Style
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="header_display_mode"
                          value="landscape"
                          checked={currentNewsletter.header_display_mode !== 'full'}
                          onChange={(e) => updateNewsletterField('header_display_mode', e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">Landscape Photo</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="header_display_mode"
                          value="full"
                          checked={currentNewsletter.header_display_mode === 'full'}
                          onChange={(e) => updateNewsletterField('header_display_mode', e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">Full Photo</span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Landscape: Fixed height with cropping. Full: Shows complete image height.
                    </p>
                  </div>
                )}
                
                {currentNewsletter.header_image_id && (
                  <div className="mt-2">
                    <img
                      src={`/api/newsletter/asset/${currentNewsletter.header_image_id}`}
                      alt="Header"
                      className={`max-w-full object-cover rounded ${
                        currentNewsletter.header_display_mode === 'full' 
                          ? 'h-auto' 
                          : 'h-32'
                      }`}
                      style={currentNewsletter.header_display_mode !== 'full' ? {
                        objectPosition: 'center'
                      } : {}}
                    />
                  </div>
                )}
              </div>

              {/* Sections */}
              <div>
                <h4 className="text-lg font-medium mb-4">Newsletter Sections</h4>
                <div className="space-y-4">
                  {currentNewsletter.sections.map((s,i)=>(
                    <div key={s.key} className="p-4 rounded-2xl border">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold">{s.title}</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-xs">Show</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={s.enabled}
                              onChange={(e) => updateSection(i, 'enabled', e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`w-11 h-6 rounded-full transition-colors ${
                              s.enabled ? 'bg-blue-600' : 'bg-gray-200'
                            }`}>
                              <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                                s.enabled ? 'translate-x-5' : 'translate-x-0.5'
                              } mt-0.5`}></div>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Column Layout Options */}
                      <div className="flex items-center gap-4 mb-3 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Layout:</span>
                        <div className="flex gap-2">
                          {[1, 2, 3].map(cols => (
                            <button
                              key={cols}
                              onClick={() => updateSection(i, 'columns', cols)}
                              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                (s.columns || 1) === cols
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              {cols} Column{cols > 1 ? 's' : ''}
                            </button>
                          ))}
                        </div>
                        
                        {/* Section Image Upload */}
                        <div className="ml-auto">
                          <label className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors cursor-pointer">
                            📷 Section Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => uploadSectionHeaderImage(e.target.files[0], i)}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>

                      {/* Section Header Image Display */}
                      {s.header_image_id && (
                        <div className="mb-3">
                          <img
                            src={`/api/newsletter/asset/${s.header_image_id}`}
                            alt="Section Header"
                            className="max-w-full h-24 object-cover rounded border"
                          />
                          <button
                            onClick={() => updateSection(i, 'header_image_id', null)}
                            className="mt-1 text-xs text-red-600 hover:text-red-800"
                          >
                            Remove Image
                          </button>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          onClick={() => copyFromSettings(i)}
                          className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                          title="Copy default template from settings"
                        >
                          📋 Copy from Settings
                        </button>
                        
                        {/* Image Upload - exclude reminders and board_meeting sections */}
                        {s.key !== 'reminders' && s.key !== 'board_meeting' && (
                          <label className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors cursor-pointer">
                            📷 Add Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => uploadSectionImage(e.target.files[0], i)}
                              className="hidden"
                            />
                          </label>
                        )}
                        
                        {/* Board Meeting Dropdown */}
                        {s.key === 'board_meeting' && (
                          <div className="flex flex-wrap gap-2">
                            {upcomingMeetings.length > 0 ? (
                              <>
                                {/* Multiple meeting selectors for multi-column layout */}
                                {Array.from({ length: s.columns || 1 }, (_, colIndex) => (
                                  <div key={colIndex} className="flex gap-2 items-center">
                                    <select
                                      onChange={(e) => selectBoardMeeting(i, e.target.value, colIndex)}
                                      className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full border-none cursor-pointer"
                                      value={selectedMeetingIds[`${i}-${colIndex}`] || ""}
                                    >
                                      <option value="">📅 Select Meeting {colIndex + 1}</option>
                                      {upcomingMeetings.slice(0, 5).map(meeting => {
                                        const meetingId = meeting.id || meeting._id || meeting.event_id;
                                        return (
                                        <option key={meetingId} value={meetingId}>
                                          {(() => {
                                            const dateField = meeting.start_date || meeting.date || meeting.start_datetime || meeting.event_date;
                                            let displayDate = 'No date';
                                            if (dateField) {
                                              try {
                                                const date = new Date(dateField);
                                                if (!isNaN(date.getTime())) {
                                                  displayDate = date.toLocaleDateString();
                                                }
                                              } catch (error) {
                                                displayDate = 'Invalid date';
                                              }
                                            }
                                            return `${displayDate} - ${meeting.title || meeting.name || 'Board Meeting'}`;
                                          })()}
                                        </option>
                                        );
                                      })}
                                    </select>
                                    
                                    {selectedMeetingIds[`${i}-${colIndex}`] && (
                                      <button
                                        onClick={() => addMeetingDetails(i, colIndex)}
                                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                                      >
                                        ➕ Add Meeting {colIndex + 1}
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </>
                            ) : (
                              <span className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                📅 No upcoming board meetings found
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Column Content Text Areas */}
                      <div className="space-y-3">
                        {Array.from({ length: s.columns || 1 }, (_, colIndex) => (
                          <div key={colIndex}>
                            {(s.columns || 1) > 1 && (
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Column {colIndex + 1} Content
                              </label>
                            )}
                            <textarea 
                              rows={6} 
                              placeholder={settings?.html_mode !== false ? "Content (HTML allowed)" : "Content (Plain text - bullet points and line breaks supported)"}
                              value={s.column_content?.[colIndex] || s.html || ''}
                              onChange={(e) => updateSectionColumnContent(i, colIndex, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      
                      {/* Helper text for sections */}
                      <div className="mt-2 text-xs text-gray-500">
                        {s.key === 'board_meeting' && (
                          <p>💡 Select meetings from dropdowns, then click "Add Meeting" to auto-fill each column</p>
                        )}
                        {s.key === 'reminders' && (
                          <p>💡 This section helps residents find important info on the dashboard</p>
                        )}
                        {s.key === 'spotlight' && (
                          <p>💡 Feature a community group, volunteer, or upcoming social event</p>
                        )}
                        {(s.key !== 'board_meeting' && s.key !== 'reminders' && s.key !== 'spotlight') && (
                          <p>💡 Use "Section Image" to include photos. {settings?.html_mode !== false ? 'HTML tags supported for formatting.' : 'Plain text mode - use bullet points and line breaks for formatting.'}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <button
                  onClick={saveNewsletter}
                  disabled={saving}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 disabled:opacity-50 flex-1 sm:flex-none"
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={publishNewsletter}
                  disabled={saving}
                  className="px-6 py-2 text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50 flex-1 sm:flex-none"
                  style={{ backgroundColor: brandColor }}
                >
                  {saving ? 'Publishing...' : 'Publish Newsletter'}
                </button>
                <button
                  onClick={deleteCurrentNewsletter}
                  disabled={saving}
                  className="px-6 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 disabled:opacity-50 flex-1 sm:flex-none"
                  title="Delete this draft newsletter"
                >
                  {saving ? 'Deleting...' : 'Delete Draft'}
                </button>
              </div>

              {/* Pro Tip Section */}
              <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-lg">💡</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-purple-900 mb-2">Pro Tip: AI-Enhanced HTML Newsletters</h4>
                    <p className="text-purple-700 mb-3">
                      Once you compose a section of a newsletter, because it is HTML enabled, if you want it to really stand out you can use an AI chatbot to create an HTML version of your section for a newsletter.
                    </p>
                    
                    <div className="bg-white border border-purple-200 rounded-lg p-4 mb-4">
                      <h5 className="font-medium text-purple-800 mb-2">Example prompt to try:</h5>
                      <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm font-mono text-gray-700 mb-3">
                        "type out this paragraph with html coding and proper formatting to show on a small section of a webpage to make it look very appealing for a newsletter:
                        <br /><br />
                        WE ARE ON FIRE WITH MILTON STILL UNDEFEATED AGAINST NAVARRE. 5-0 BABY! LET'S GIVE IT UP TO OUR MILTON HEROES! Andy, John, Grey, and Sammy right here from our neighborhood!"
                      </div>
                      
                      <h5 className="font-medium text-purple-800 mb-2">Sample AI output:</h5>
                      <div className="bg-gray-50 border border-gray-200 rounded p-4 overflow-auto max-h-96">
                        <div dangerouslySetInnerHTML={{
                          __html: `
<section class="milton-news-card" aria-label="Milton victory update">
  <style>
    .milton-news-card {
      --bg: linear-gradient(135deg, #ffefba 0%, #ffffff 50%, #ffd1d1 100%);
      --accent: #c81439;
      --accent-2: #133e87;
      --text: #1b1b1b;
      --pill: #ffe8ec;
      --shadow: 0 10px 25px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.6);
      font-family: system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji","Segoe UI Emoji", sans-serif;
      background: var(--bg);
      border-radius: 20px;
      box-shadow: var(--shadow);
      padding: 20px 22px;
      max-width: 560px;
      margin: 0 auto;
      position: relative;
      overflow: hidden;
    }
    .milton-news-card:before {
      content: "";
      position: absolute;
      right: -40px; top: -40px;
      width: 160px; height: 160px;
      background: radial-gradient(closest-side, rgba(200,20,57,.18), transparent 70%);
      transform: rotate(15deg);
    }
    .milton-kicker {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #fff;
      color: var(--accent-2);
      border: 1px solid rgba(19,62,135,.12);
      padding: 6px 12px;
      border-radius: 999px;
      font-weight: 700;
      letter-spacing: .02em;
      box-shadow: 0 4px 12px rgba(19,62,135,.08);
      text-transform: uppercase;
      font-size: 12px;
    }
    .milton-kicker .record {
      background: var(--pill);
      color: var(--accent);
      padding: 4px 10px;
      border-radius: 999px;
      border: 1px solid rgba(200,20,57,.25);
      font-size: 12px;
    }
    .milton-title {
      margin: 14px 0 6px;
      font-size: clamp(22px, 4.5vw, 30px);
      line-height: 1.15;
      font-weight: 900;
      color: var(--text);
      letter-spacing: .3px;
      text-wrap: balance;
    }
    .milton-title em {
      color: var(--accent);
      font-style: normal;
      text-shadow: 0 1px 0 rgba(255,255,255,.8);
    }
    .milton-subtitle {
      margin: 0 0 14px;
      color: #333;
      font-weight: 600;
      letter-spacing: .25px;
    }
    .milton-names {
      display: flex; flex-wrap: wrap; gap: 8px;
      margin: 10px 0 0; padding: 0; list-style: none;
    }
    .milton-names li {
      background: #fff;
      border: 1px solid rgba(0,0,0,.06);
      border-radius: 12px;
      padding: 6px 10px;
      box-shadow: 0 3px 10px rgba(0,0,0,.06);
      font-weight: 600;
      color: #222;
    }
    /* subtle entrance */
    @keyframes popIn {
      from { transform: translateY(8px); opacity: 0; }
      to   { transform: translateY(0);  opacity: 1; }
    }
    .milton-news-card > * { animation: popIn .5s ease both; }
    .milton-title { animation-delay: .05s; }
    .milton-subtitle { animation-delay: .1s; }
    .milton-names { animation-delay: .15s; }
  </style>

  <span class="milton-kicker" aria-label="Milton vs Navarre status">
    🔥 We Are On Fire!
    <span class="record" aria-label="Current record">5–0</span>
  </span>

  <h2 class="milton-title">
    <em>Milton</em> still undefeated against Navarre — <em>5–0</em>, baby!
  </h2>

  <p class="milton-subtitle">Let's give it up for our Milton heroes from right here in our neighborhood:</p>

  <ul class="milton-names" aria-label="Player list">
    <li>Andy</li>
    <li>John</li>
    <li>Grey</li>
    <li>Sammy</li>
  </ul>
</section>
                          `
                        }} />
                      </div>
                    </div>
                    
                    <p className="text-sm text-purple-600">
                      ✨ <strong>Tip:</strong> Copy the HTML output from your AI chatbot and paste it directly into any newsletter section to create eye-catching, professionally formatted content that will make your community newsletters truly stand out!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Preview</h3>
        </div>
        <div className="p-6">
          {currentNewsletter ? (
            <NewsletterPreview 
              newsletter={currentNewsletter} 
              logoUrl={logoUrl}
              brandColor={brandColor}
              settings={settings}
              hoaData={hoaData}
            />
          ) : (
            <div className="text-center text-gray-500 py-8">
              Select or create a newsletter to see preview
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Newsletter Preview Component
const NewsletterPreview = ({ newsletter, logoUrl, brandColor, settings, hoaData }) => {
  return (
    <div style={{ fontFamily: 'system-ui, Arial, sans-serif', maxWidth: '600px', margin: 'auto' }}>
      {/* HOA Logo Header */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        {hoaData?.branding?.logo_base64 ? (
          <img
            src={`data:${hoaData.branding.logo_content_type || 'image/png'};base64,${hoaData.branding.logo_base64}`}
            alt="HOA Logo"
            style={{ maxWidth: '200px', height: 'auto', marginBottom: '10px' }}
          />
        ) : logoUrl ? (
          <img
            src={logoUrl}
            alt="HOA Logo"
            style={{ maxWidth: '200px', height: 'auto', marginBottom: '10px' }}
          />
        ) : (
          <div style={{ 
            backgroundColor: brandColor, 
            color: 'white', 
            padding: '20px', 
            fontSize: '24px', 
            fontWeight: 'bold', 
            borderRadius: '10px', 
            display: 'inline-block',
            marginBottom: '10px'
          }}>
            {hoaData?.name || 'HOA Community'}
          </div>
        )}
      </div>
      
      {/* Header Image */}
      {newsletter.header_image_id && (
        <img
          src={`/api/newsletter/asset/${newsletter.header_image_id}`}
          alt="Newsletter Header"
          style={newsletter.header_display_mode === 'full' ? {
            width: '100%', 
            borderRadius: '8px', 
            marginBottom: '20px' 
          } : {
            width: '100%', 
            height: '200px',
            objectFit: 'cover',
            objectPosition: 'center',
            borderRadius: '8px', 
            marginBottom: '20px' 
          }}
        />
      )}
      
      {/* Title */}
      <h1 style={{ color: brandColor, fontSize: '28px', marginBottom: '20px' }}>
        {newsletter.title}
      </h1>
      
      {/* Sections */}
      {newsletter.sections.filter(section => section.enabled).map(section => (
        <div key={section.key} style={{ marginBottom: '32px' }}>
          {/* Branded Section Header */}
          <div 
            style={{ 
              background: `linear-gradient(135deg, ${brandColor} 0%, ${brandColor}dd 100%)`,
              color: 'white',
              padding: '16px 24px',
              borderRadius: '8px',
              marginBottom: '16px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <h2 style={{ 
              color: 'white', 
              fontSize: '20px', 
              margin: '0',
              fontWeight: 'bold',
              letterSpacing: '0.5px'
            }}>
              {section.title}
            </h2>
          </div>
          
          {/* Section Image - appears AFTER header, BEFORE content */}
          {section.header_image_id && (
            <div style={{ marginTop: '16px', marginBottom: '20px', textAlign: 'center' }}>
              <img
                src={`/api/newsletter/asset/${section.header_image_id}`}
                alt={`${section.title} Image`}
                style={{ 
                  maxWidth: '60%',
                  maxHeight: '400px',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          )}
          
          {/* Section Content with Column Layout */}
          <div 
            style={{ 
              display: section.columns > 1 ? 'grid' : 'block',
              gridTemplateColumns: section.columns > 1 ? `repeat(${section.columns}, 1fr)` : 'none',
              gap: section.columns > 1 ? '20px' : '0'
            }}
          >
            {section.columns > 1 ? (
              // Multi-column content
              Array.from({ length: section.columns }, (_, colIndex) => (
                <div key={colIndex} style={{ padding: '0 10px' }}>
                  {settings?.html_mode === false ? (
                    <div style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                      {section.column_content?.[colIndex] || ''}
                    </div>
                  ) : (
                    <div dangerouslySetInnerHTML={{ 
                      __html: section.column_content?.[colIndex] || '' 
                    }} />
                  )}
                </div>
              ))
            ) : (
              // Single column content
              <div>
                {settings?.html_mode === false ? (
                  <div style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                    {section.column_content?.[0] || section.html || ''}
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ 
                    __html: section.column_content?.[0] || section.html || '' 
                  }} />
                )}
              </div>
            )}
          </div>
        </div>
      ))}
      
      {/* Footer */}
      <div style={{ 
        marginTop: '40px', 
        padding: '20px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        textAlign: 'center',
        fontSize: '14px',
        color: '#666'
      }}>
        <p>{settings?.footer_text || "This newsletter was sent by your HOA management team."}</p>
      </div>
    </div>
  );
};

// Newsletter Archive Component
const NewsletterArchive = ({ user, hoaData }) => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    q: '',
    date_from: '',
    date_to: '',
    status: 'published'
  });

  useEffect(() => {
    loadNewsletters();
  }, []);

  const loadNewsletters = async () => {
    try {
      const params = new URLSearchParams();
      params.append('hoa_id', user.hoa_id);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`/api/newsletter?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewsletters(data.newsletters);
      }
    } catch (error) {
      console.error('Error loading newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setLoading(true);
    loadNewsletters();
  };

  // Helper function to open newsletter PDF using presigned URL
  const openNewsletterPDF = async (newsletterId) => {
    try {
      const response = await api.get(`/api/newsletter/${newsletterId}/pdf`);
      if (response.data && response.data.url) {
        const newWindow = window.open(response.data.url, '_blank', 'noopener,noreferrer');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          alert('Please allow popups for this site to view newsletters');
        }
        return true;
      } else {
        alert('PDF not available. Please regenerate using the "Regenerate PDF" button.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching PDF:', error);
      alert('Failed to load PDF: ' + (error.response?.data?.detail || error.message));
      return false;
    }
  };

  const viewNewsletter = async (newsletter) => {
    try {
      // If newsletter has pdf_url OR is published, fetch presigned URL and open
      if (newsletter.pdf_url || newsletter.status === 'published') {
        const success = await openNewsletterPDF(newsletter.id);
        if (!success) {
          // If PDF fetch fails, try loading full newsletter for preview modal
          try {
            const response = await api.get(`/api/newsletter/${newsletter.id}`);
            if (response.data) {
              setSelectedNewsletter(response.data);
              setViewModalOpen(true);
            }
          } catch (err) {
            console.error('Fallback preview also failed:', err);
          }
        }
        return;
      }
      
      // Otherwise load full newsletter data for preview
      const response = await api.get(`/api/newsletter/${newsletter.id}`);
      
      if (response.data) {
        const fullNewsletter = response.data;
        // Fallback to preview modal
        setSelectedNewsletter(fullNewsletter);
        setViewModalOpen(true);
      } else {
        alert('Failed to load newsletter content');
      }
    } catch (error) {
      console.error('Error loading newsletter:', error);
      alert('Error loading newsletter content: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadNewsletter = async (newsletter) => {
    try {
      // Load full newsletter data
      const response = await fetch(`/api/newsletter/${newsletter.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const fullNewsletter = await response.json();
        
        // Debug: Log the newsletter data to see structure
        console.log('Full newsletter data:', fullNewsletter);
        
        // Generate HTML content for PDF
        let logoHtml = '';
        const brandColor = hoaData?.branding?.primary_color || '#3b82f6';
        
        // Handle logo display - prioritize logo_url from backend branding endpoint
        if (hoaData?.branding?.logo_url || hoaData?.logo_url) {
          // Use logo_url from backend (already returns proper URL endpoint)
          const logoUrl = hoaData?.branding?.logo_url || hoaData?.logo_url;
          const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${window.location.origin}${logoUrl}`;
          logoHtml = `<img src="${fullLogoUrl}" alt="HOA Logo" class="logo" onError="this.style.display='none'">`;
        } else if (hoaData?.branding?.logo_base64) {
          // Fallback to base64 if available
          const logoDataUrl = `data:${hoaData.branding.logo_content_type || 'image/png'};base64,${hoaData.branding.logo_base64}`;
          logoHtml = `<img src="${logoDataUrl}" alt="HOA Logo" class="logo">`;
        } else {
          // Fallback to text-based logo
          const hoaName = hoaData?.name || 'HOA Community';
          logoHtml = `<div class="text-logo" style="background-color: ${brandColor}; color: white; padding: 20px; font-size: 24px; font-weight: bold; border-radius: 10px; display: inline-block;">${hoaName}</div>`;
        }
        
        // Fetch header image as base64 if it exists
        let headerImageHtml = '';
        if (fullNewsletter.header_image_id) {
          try {
            const imageResponse = await fetch(`/api/newsletter/asset/${fullNewsletter.header_image_id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
              }
            });
            
            if (imageResponse.ok) {
              const imageBlob = await imageResponse.blob();
              const imageBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(imageBlob);
              });
              
              headerImageHtml = `<img 
                src="${imageBase64}" 
                alt="Newsletter Header" 
                style="${fullNewsletter.header_display_mode === 'full' ? 
                  'width: 100%; border-radius: 8px; margin-bottom: 20px;' : 
                  'width: 100%; height: 200px; object-fit: cover; object-position: center; border-radius: 8px; margin-bottom: 20px;'}"
              />`;
            }
          } catch (err) {
            console.error('Error fetching header image:', err);
          }
        }
        
        // Fetch section images as base64 if they exist
        const sectionImageCache = {};
        if (fullNewsletter.sections && Array.isArray(fullNewsletter.sections)) {
          for (const section of fullNewsletter.sections) {
            if (section.header_image_id && !sectionImageCache[section.header_image_id]) {
              try {
                const imageResponse = await fetch(`/api/newsletter/asset/${section.header_image_id}`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
                  }
                });
                
                if (imageResponse.ok) {
                  const imageBlob = await imageResponse.blob();
                  const imageBase64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(imageBlob);
                  });
                  
                  sectionImageCache[section.header_image_id] = imageBase64;
                }
              } catch (err) {
                console.error(`Error fetching section image ${section.header_image_id}:`, err);
              }
            }
          }
        }
        
        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>${fullNewsletter.title}</title>
            <style>
              body { 
                font-family: system-ui, -apple-system, Arial, sans-serif; 
                margin: 0; 
                padding: 40px; 
                line-height: 1.6;
                background-color: #f5f5f5;
              }
              .container {
                max-width: 600px;
                margin: auto;
                background-color: white;
                padding: 40px;
                border-radius: 12px;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              }
              .header { 
                text-align: center; 
                margin-bottom: 40px; 
              }
              .logo { 
                max-width: 200px; 
                height: auto; 
                margin-bottom: 20px; 
              }
              .text-logo { 
                background-color: ${brandColor};
                color: white;
                padding: 20px;
                font-size: 24px;
                font-weight: bold;
                border-radius: 10px;
                display: inline-block;
                margin-bottom: 20px;
              }
              .header-image {
                width: 100%;
                border-radius: 8px;
                margin-bottom: 30px;
              }
              .title { 
                color: ${brandColor}; 
                font-size: 32px; 
                margin: 20px 0;
                font-weight: bold;
                line-height: 1.3;
              }
              .issue-date { 
                color: #666; 
                font-size: 16px;
                margin-bottom: 10px;
              }
              .section { 
                margin: 40px 0; 
              }
              .section-header {
                background: linear-gradient(135deg, ${brandColor} 0%, ${brandColor}dd 100%);
                color: white;
                padding: 16px 24px;
                border-radius: 8px;
                margin-bottom: 20px;
                text-align: center;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              }
              .section-title { 
                color: white; 
                font-size: 22px; 
                font-weight: bold;
                margin: 0;
                letter-spacing: 0.5px;
              }
              .section-image {
                max-width: 60%;
                max-height: 400px;
                height: auto;
                display: block;
                margin: 20px auto;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              .content { 
                color: #333; 
                font-size: 16px;
                line-height: 1.8;
              }
              .content p {
                margin-bottom: 16px;
              }
              .content h1, .content h2, .content h3 {
                color: ${brandColor};
                margin-top: 24px;
                margin-bottom: 12px;
              }
              .content ul, .content ol {
                margin-left: 20px;
                margin-bottom: 16px;
              }
              .content li {
                margin-bottom: 8px;
              }
              .multi-column {
                display: grid;
                gap: 20px;
                margin-top: 20px;
              }
              .column {
                padding: 0 10px;
              }
              .footer { 
                margin-top: 50px; 
                padding: 30px; 
                background-color: #f8f9fa;
                border-radius: 8px;
                font-size: 14px; 
                color: #666; 
                text-align: center;
                border-top: 3px solid ${brandColor};
              }
              @media print {
                body {
                  background-color: white;
                  padding: 0;
                }
                .container {
                  box-shadow: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                ${logoHtml}
                ${headerImageHtml ? `<img src="${headerImageHtml.match(/src="([^"]*)"/)?.[1] || ''}" class="header-image" alt="Newsletter Header" />` : ''}
                <h1 class="title">${fullNewsletter.title}</h1>
                <div class="issue-date">Issue Date: ${fullNewsletter.issue_date ? new Date(fullNewsletter.issue_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : new Date(fullNewsletter.created_at || new Date()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                ${fullNewsletter.status ? `<div style="color: #888; font-size: 14px; font-weight: 500;">Status: ${fullNewsletter.status.toUpperCase()}</div>` : ''}
              </div>
              
              ${(() => {
                // Handle different possible content structures with enhanced styling
                let contentHTML = '';
                
                // Check for sections array (newsletter model uses 'html' property)
                if (fullNewsletter.sections && Array.isArray(fullNewsletter.sections)) {
                  contentHTML = fullNewsletter.sections
                    .filter(section => section.enabled !== false)
                    .map(section => {
                      // Build section image HTML using cached base64 images
                      let sectionImageHTML = '';
                      if (section.header_image_id && sectionImageCache[section.header_image_id]) {
                        sectionImageHTML = `<img 
                          src="${sectionImageCache[section.header_image_id]}" 
                          class="section-image" 
                          alt="${section.title} Image" 
                          style="max-width: 60%; max-height: 400px; height: auto; display: block; margin: 20px auto; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
                        />`;
                      }
                      
                      // Handle multi-column layout
                      const columns = section.columns || 1;
                      let sectionContentHTML = '';
                      
                      if (columns > 1 && section.column_content) {
                        sectionContentHTML = `<div class="multi-column" style="grid-template-columns: repeat(${columns}, 1fr);">`;
                        for (let i = 0; i < columns; i++) {
                          const columnContent = section.column_content[i] || '';
                          sectionContentHTML += `<div class="column content">${columnContent}</div>`;
                        }
                        sectionContentHTML += '</div>';
                      } else {
                        const content = section.column_content?.[0] || section.html || '';
                        sectionContentHTML = `<div class="content">${content}</div>`;
                      }
                      
                      return `
                        <div class="section">
                          <div class="section-header">
                            <h2 class="section-title">${section.title || 'Untitled Section'}</h2>
                          </div>
                          ${sectionImageHTML}
                          ${sectionContentHTML}
                        </div>
                      `;
                    }).join('');
                }
                // Check for direct content properties
                else if (fullNewsletter.content || fullNewsletter.body || fullNewsletter.html_content) {
                  contentHTML = `
                    <div class="section">
                      <div class="content">${fullNewsletter.content || fullNewsletter.body || fullNewsletter.html_content}</div>
                    </div>
                  `;
                }
                // Check for template_data with sections
                else if (fullNewsletter.template_data && fullNewsletter.template_data.sections) {
                  contentHTML = fullNewsletter.template_data.sections
                    .filter(section => section.enabled !== false)
                    .map(section => `
                      <div class="section">
                        <div class="section-header">
                          <h2 class="section-title">${section.title || 'Untitled Section'}</h2>
                        </div>
                        <div class="content">${section.html || section.content || ''}</div>
                      </div>
                    `).join('');
                }
                // Fallback: show message about no content
                else {
                  contentHTML = `
                    <div class="section">
                      <div class="section-header">
                        <h2 class="section-title">Newsletter Content</h2>
                      </div>
                      <div class="content">
                        <p>No content available for this newsletter.</p>
                        <p><em>This newsletter may be a draft or may not have any sections enabled.</em></p>
                      </div>
                    </div>
                  `;
                }
                
                return contentHTML;
              })()}
              
              <div class="footer">
                <p style="margin: 0; font-weight: 500;">${hoaData?.name || 'HOA Community'} Newsletter</p>
                <p style="margin: 8px 0 0 0; font-size: 12px;">This newsletter was sent by your HOA management team.</p>
              </div>
            </div>
          </body>
          </html>
        `;
        
        // Create blob and download
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Create filename from newsletter title and date
        const date = fullNewsletter.issue_date ? new Date(fullNewsletter.issue_date).toISOString().split('T')[0] : 'undated';
        const title = fullNewsletter.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        link.download = `newsletter_${date}_${title}.html`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } else {
        alert('Failed to load newsletter content for download');
      }
    } catch (error) {
      console.error('Error downloading newsletter:', error);
      alert('Error downloading newsletter');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading newsletters...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4">Search & Filters</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search title..."
            value={filters.q}
            onChange={(e) => handleFilterChange('q', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <button
            onClick={applyFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Search
          </button>
        </div>
      </div>

      {/* Newsletter List */}
      <div className="space-y-4">
        {newsletters.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No newsletters found. Try adjusting your search criteria.
          </div>
        ) : (
          newsletters.map(newsletter => (
            <div key={newsletter.id} className="bg-white rounded-lg border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{newsletter.title}</h3>
                  <div className="text-sm text-gray-500 mt-1">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      newsletter.status === 'published' ? 'bg-green-100 text-green-800' :
                      newsletter.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {newsletter.status}
                    </span>
                    <span className="ml-2">
                      {newsletter.issue_date ? 
                        new Date(newsletter.issue_date).toLocaleDateString() : 
                        'No date set'
                      }
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {newsletter.pdf_url ? (
                    <>
                      <button
                        onClick={() => openNewsletterPDF(newsletter.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        📄 View PDF
                      </button>
                      <button
                        onClick={() => openNewsletterPDF(newsletter.id)}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center"
                      >
                        📥 Download PDF
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => viewNewsletter(newsletter)}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        👁️ View
                      </button>
                      {newsletter.status === 'published' && (
                        <button
                          onClick={async () => {
                            try {
                              const response = await api.post(`/api/newsletter/${newsletter.id}/regenerate-pdf`);
                              if (response.data) {
                                const data = response.data;
                                // Open the PDF immediately after generation using presigned URL
                                if (data.presigned_url) {
                                  window.open(data.presigned_url, '_blank', 'noopener,noreferrer');
                                } else {
                                  // Fallback: fetch presigned URL
                                  await openNewsletterPDF(newsletter.id);
                                }
                                // Refresh the list to show PDF button
                                loadNewsletters();
                              } else {
                                alert('Failed to generate PDF');
                              }
                            } catch (error) {
                              console.error('Error generating PDF:', error);
                              alert('Error generating PDF');
                            }
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                          title="Generate PDF for this published newsletter"
                        >
                          🔄 Generate PDF
                        </button>
                      )}
                      <button
                        onClick={() => downloadNewsletter(newsletter)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        title="Download newsletter as HTML file (can be saved as PDF)"
                      >
                        📥 Download HTML
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Newsletter View Modal */}
      {viewModalOpen && selectedNewsletter && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {selectedNewsletter.title}
              </h3>
              <button
                onClick={() => setViewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <NewsletterPreview 
                newsletter={selectedNewsletter} 
                logoUrl={hoaData?.branding?.logo_url || hoaData?.logo_url}
                brandColor={hoaData?.branding?.primary_color}
                settings={{ html_mode: true }}
                hoaData={hoaData}
              />
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setViewModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Newsletter Settings Component
const NewsletterSettings = ({ user, hoaData, settings, onSettingsUpdate, canEdit }) => {
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [skipMonth, setSkipMonth] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({ ...settings });
    }
  }, [settings]);

  useEffect(() => {
    if (canEdit) {
      loadAvailableUsers();
    }
  }, [canEdit]);

  const loadAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await fetch(`/api/users/search?query=&limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateSection = (sectionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      default_sections: prev.default_sections.map((section, index) => 
        index === sectionIndex ? { ...section, [field]: value } : section
      )
    }));
  };

  const saveSettings = async () => {
    if (!canEdit || !formData) return;
    
    try {
      setSaving(true);
      const response = await fetch(`/api/newsletter/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        alert('Settings saved successfully!');
        onSettingsUpdate();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const addSkipMonth = async () => {
    if (!skipMonth || !canEdit) return;
    
    try {
      const response = await fetch(`/api/newsletter/skip/${user.hoa_id}/${skipMonth}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        setSkipMonth('');
        onSettingsUpdate();
      }
    } catch (error) {
      console.error('Error adding skip month:', error);
    }
  };

  const removeSkipMonth = async (month) => {
    if (!canEdit) return;
    
    try {
      const response = await fetch(`/api/newsletter/skip/${user.hoa_id}/${month}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      if (response.ok) {
        onSettingsUpdate();
      }
    } catch (error) {
      console.error('Error removing skip month:', error);
    }
  };

  const addAllowedUser = (userId) => {
    if (!userId || !formData) return;
    
    const currentUsers = formData.allowed_individuals || [];
    if (!currentUsers.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        allowed_individuals: [...currentUsers, userId]
      }));
    }
  };

  const removeAllowedUser = (userId) => {
    if (!formData) return;
    
    setFormData(prev => ({
      ...prev,
      allowed_individuals: (prev.allowed_individuals || []).filter(id => id !== userId)
    }));
  };

  const getUserDisplayName = (userId) => {
    const foundUser = availableUsers.find(u => u.id === userId);
    if (foundUser) {
      return `${foundUser.first_name} ${foundUser.last_name} (${foundUser.email})`;
    }
    return `User ID: ${userId}`;
  };

  if (!settings) {
    return <div className="text-center py-8">Loading settings...</div>;
  }

  if (!formData) {
    return <div className="text-center py-8">Initializing settings...</div>;
  }

  if (!canEdit) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">You don't have permission to modify newsletter settings.</p>
        <p className="text-sm text-gray-500 mt-2">Contact your board members for access.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">General Settings</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => updateField('enabled', e.target.checked)}
              />
              <span className="font-medium">Enable Newsletter System</span>
            </label>
          </div>
          
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.html_mode !== false}
                onChange={(e) => updateField('html_mode', e.target.checked)}
              />
              <span className="font-medium">HTML Mode</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              When enabled, users can use HTML formatting. When disabled, content shows as plain text with line breaks and bullet points.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequency
            </label>
            <select
              value={formData.frequency}
              onChange={(e) => updateField('frequency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="off">Off</option>
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          
          {formData.frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Day
              </label>
              <select
                value={formData.monthly_day}
                onChange={(e) => updateField('monthly_day', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value={1}>1st of the month</option>
                <option value={15}>15th of the month</option>
              </select>
            </div>
          )}
          
          {formData.frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Weekly Day
              </label>
              <select
                value={formData.weekly_day}
                onChange={(e) => updateField('weekly_day', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Permission Settings */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Permission Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.board_only_create}
                onChange={(e) => updateField('board_only_create', e.target.checked)}
              />
              <span className="font-medium">Only board members can create newsletters</span>
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed Roles (comma-separated)
            </label>
            <input
              type="text"
              value={formData.allowed_roles?.join(', ') || ''}
              onChange={(e) => updateField('allowed_roles', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="board_member, president, secretary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Authorized Users
            </label>
            
            {/* User Selection Dropdown */}
            <div className="flex gap-2 mb-3">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addAllowedUser(e.target.value);
                    e.target.value = ''; // Reset dropdown
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                disabled={loadingUsers}
              >
                <option value="">
                  {loadingUsers ? 'Loading users...' : 'Select user to add'}
                </option>
                {availableUsers
                  .filter(u => !(formData.allowed_individuals || []).includes(u.id))
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
              </select>
            </div>
            
            {/* Currently Allowed Users */}
            {formData.allowed_individuals && formData.allowed_individuals.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Currently authorized users:</p>
                {formData.allowed_individuals.map(userId => (
                  <div key={userId} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm">{getUserDisplayName(userId)}</span>
                    <button
                      onClick={() => removeAllowedUser(userId)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Skip Months */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Skip Months</h3>
        <div className="flex gap-2 mb-4">
          <input
            type="month"
            value={skipMonth}
            onChange={(e) => setSkipMonth(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <button
            onClick={addSkipMonth}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Skip Month
          </button>
        </div>
        
        {formData.skip_months?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">Skipped months:</p>
            <div className="flex flex-wrap gap-2">
              {formData.skip_months.map(month => (
                <span
                  key={month}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm"
                >
                  {month}
                  <button
                    onClick={() => removeSkipMonth(month)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer Settings */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Newsletter Footer</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Footer Text
          </label>
          <textarea
            value={formData.footer_text || ''}
            onChange={(e) => updateField('footer_text', e.target.value)}
            rows={3}
            placeholder="This newsletter was sent by your HOA management team."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="mt-2 text-xs text-gray-500">
            This text will appear at the bottom of all newsletters. Leave empty to use the default message.
          </div>
        </div>
      </div>

      {/* Default Sections */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Default Newsletter Sections</h3>
        
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">📝 How Default Sections Work:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• These templates automatically populate new newsletters when created</li>
            <li>• Used by the scheduler for auto-generated monthly/weekly newsletters</li>
            <li>• Click "Copy from Settings" in any newsletter section to use these templates</li>
            <li>• Edit these to customize your community's standard newsletter format</li>
          </ul>
        </div>
        
        <div className="space-y-4">
          {formData.default_sections?.map((section, index) => (
            <div key={section.key} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateSection(index, 'title', e.target.value)}
                  className="font-medium text-lg border-none outline-none bg-transparent"
                />
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={section.enabled}
                    onChange={(e) => updateSection(index, 'enabled', e.target.checked)}
                  />
                  <span className="text-sm">Include by default</span>
                </label>
              </div>
              <textarea
                value={section.html}
                onChange={(e) => updateSection(index, 'html', e.target.value)}
                rows={4}
                placeholder="Default content template (HTML allowed)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
              />
              <div className="mt-2 text-xs text-gray-500">
                This template will be used when creating new newsletters or using "Copy from Settings"
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

// Reaction Buttons Component
const ReactionButtons = ({ sectionId, sectionTitle, user }) => {
  const [reactions, setReactions] = useState({ heart: 0, happy: 0, exclamation: 0 });
  const [userReaction, setUserReaction] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReactions();
  }, [sectionId]);

  const loadReactions = async () => {
    try {
      // Load existing reactions for this section
      const response = await fetch(`/api/newsletter-reactions/${sectionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReactions(data.reactions || { heart: 0, happy: 0, exclamation: 0 });
        setUserReaction(data.user_reaction || null);
      }
    } catch (error) {
      console.error('Error loading reactions:', error);
    }
  };

  const handleReaction = async (reactionType) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/newsletter-reactions/${sectionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: JSON.stringify({
          reaction_type: reactionType,
          section_title: sectionTitle,
          hoa_id: user.hoa_id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReactions(data.reactions);
        setUserReaction(data.user_reaction);
      } else {
        // For now, just update locally if backend isn't ready
        const newReactions = { ...reactions };
        
        // Remove previous reaction if any
        if (userReaction) {
          newReactions[userReaction] = Math.max(0, newReactions[userReaction] - 1);
        }
        
        // Add new reaction
        if (userReaction === reactionType) {
          // If clicking same reaction, remove it
          setUserReaction(null);
        } else {
          newReactions[reactionType] = (newReactions[reactionType] || 0) + 1;
          setUserReaction(reactionType);
        }
        
        setReactions(newReactions);
      }
    } catch (error) {
      console.error('Error submitting reaction:', error);
      
      // Fallback: update locally
      const newReactions = { ...reactions };
      
      if (userReaction) {
        newReactions[userReaction] = Math.max(0, newReactions[userReaction] - 1);
      }
      
      if (userReaction === reactionType) {
        setUserReaction(null);
      } else {
        newReactions[reactionType] = (newReactions[reactionType] || 0) + 1;
        setUserReaction(reactionType);
      }
      
      setReactions(newReactions);
    } finally {
      setLoading(false);
    }
  };

  const reactionData = [
    { type: 'heart', emoji: '❤️', label: 'Love' },
    { type: 'happy', emoji: '😊', label: 'Happy' },
    { type: 'exclamation', emoji: '❗', label: 'Important' }
  ];

  return (
    <div className="mt-4 pt-3 border-t border-gray-100">
      <div className="flex items-center space-x-4">
        <span className="text-sm text-gray-500 font-medium">Reactions:</span>
        <div className="flex space-x-2">
          {reactionData.map(({ type, emoji, label }) => (
            <button
              key={type}
              onClick={() => handleReaction(type)}
              disabled={loading}
              className={`
                inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all duration-200
                ${userReaction === type 
                  ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-300 transform scale-105' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                }
                ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              title={`${label} this section`}
            >
              <span className="mr-1 text-base">{emoji}</span>
              <span>{reactions[type] || 0}</span>
            </button>
          ))}
        </div>
        {userReaction && (
          <span className="text-xs text-blue-600 italic">
            You reacted with {reactionData.find(r => r.type === userReaction)?.emoji}
          </span>
        )}
      </div>
    </div>
  );
};

// Current Issue Tab Component
const CurrentIssueTab = ({ user, hoaData, settings }) => {
  const [currentIssue, setCurrentIssue] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('CurrentIssueTab - hoaData:', hoaData);
    console.log('CurrentIssueTab - Logo available:', !!(hoaData?.branding?.logo_url || hoaData?.logo_url));
    console.log('CurrentIssueTab - Branding data:', hoaData?.branding);
    console.log('CurrentIssueTab - Logo URL:', hoaData?.branding?.logo_url || hoaData?.logo_url);
    console.log('CurrentIssueTab - HOA Code:', hoaData?.hoa_code);
  }, [hoaData]);

  useEffect(() => {
    if (user?.hoa_id) {
      loadCurrentIssue();
    }
  }, [user?.hoa_id]);

  const loadCurrentIssue = async () => {
    setLoading(true);
    try {
      console.log('Loading current issue for HOA:', user.hoa_id);
      const response = await fetch(`/api/newsletter?hoa_id=${user.hoa_id}&status=published&limit=1`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        }
      });
      
      console.log('Newsletter query response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Newsletter query data:', data);
        
        if (data.newsletters && data.newsletters.length > 0) {
          // Get the full newsletter content
          const latestNewsletter = data.newsletters[0];
          console.log('Latest newsletter:', latestNewsletter.title, latestNewsletter.id);
          
          const detailResponse = await fetch(`/api/newsletter/${latestNewsletter.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
            }
          });
          
          console.log('Newsletter detail response status:', detailResponse.status);
          
          if (detailResponse.ok) {
            const detailData = await detailResponse.json();
            console.log('Newsletter detail data:', detailData);
            console.log('Newsletter sections:', detailData.sections?.length);
            setCurrentIssue(detailData);
          } else {
            console.error('Failed to load newsletter detail:', detailResponse.status);
          }
        } else {
          console.log('No published newsletters found');
          setCurrentIssue(null);
        }
      } else {
        console.error('Failed to query newsletters:', response.status);
        setCurrentIssue(null);
      }
    } catch (error) {
      console.error('Error loading current issue:', error);
      setCurrentIssue(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading current issue...</p>
      </div>
    );
  }

  if (!currentIssue) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
        <h3 className="mt-2 text-lg font-medium text-gray-900">No Current Issue</h3>
        <p className="mt-1 text-sm text-gray-500">
          No newsletter has been published yet. Create and publish a newsletter in the Compose tab to see it here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* HOA Logo */}
            {(hoaData?.branding?.logo_url || hoaData?.logo_url) && (
              <img 
                src={hoaData?.branding?.logo_url || hoaData?.logo_url}
                alt="HOA Logo" 
                className="h-12 w-12 object-contain flex-shrink-0"
                onError={(e) => {
                  console.error('Logo failed to load:', e);
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{currentIssue.title}</h2>
              <div className="flex flex-wrap items-center mt-2 gap-x-4 gap-y-1 text-sm text-gray-500">
                {currentIssue.issue_date && (
                  <span>Issue Date: {new Date(currentIssue.issue_date).toLocaleDateString()}</span>
                )}
                {currentIssue.published_at && (
                  <span>Published: {new Date(currentIssue.published_at).toLocaleDateString()}</span>
                )}
                {currentIssue.author_name && (
                  <span>By: {currentIssue.author_name}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-3 flex-shrink-0">
            <button
              onClick={() => {
                const printWindow = window.open('', '_blank');
                const logoHtml = (hoaData?.branding?.logo_url || hoaData?.logo_url) ? 
                  `<img src="${hoaData?.branding?.logo_url || hoaData?.logo_url}" alt="HOA Logo" class="logo" />` : 
                  '';
                const primaryColor = hoaData?.branding?.primaryColor || hoaData?.branding?.primary_color || '#5A2D82';
                
                console.log('Print function - Logo available:', !!(hoaData?.branding?.logo_url || hoaData?.logo_url));
                console.log('Print function - HOA Data:', hoaData?.branding);
                
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>${currentIssue.title}</title>
                      <style>
                        @media print {
                          body { 
                            font-family: 'Georgia', 'Times New Roman', serif; 
                            margin: 0; 
                            padding: 40px;
                            line-height: 1.6;
                            color: #000;
                          }
                          .header { 
                            border-bottom: 3px solid ${primaryColor}; 
                            padding-bottom: 20px; 
                            margin-bottom: 30px; 
                            text-align: center;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 15px;
                          }
                          .logo {
                            height: 60px;
                            width: 60px;
                            object-fit: contain;
                            flex-shrink: 0;
                          }
                          .title-section {
                            text-align: center;
                            flex: 1;
                          }
                          .title { 
                            font-size: 32px; 
                            font-weight: bold; 
                            margin: 0 0 10px 0; 
                            color: ${primaryColor};
                          }
                          .meta { 
                            color: #666; 
                            margin: 0;
                            font-size: 14px;
                          }
                          .content { 
                            line-height: 1.8; 
                            font-size: 16px;
                          }
                          .section-header {
                            background: linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%);
                            color: white !important;
                            padding: 12px 20px;
                            margin: 25px 0 15px 0;
                            border-radius: 6px;
                            font-size: 18px;
                            font-weight: bold;
                            text-align: center;
                            -webkit-print-color-adjust: exact;
                            color-adjust: exact;
                            print-color-adjust: exact;
                          }
                          .section-content {
                            margin-bottom: 25px;
                            padding: 0 10px;
                          }
                          .section-content p {
                            margin: 8px 0;
                            text-align: justify;
                          }
                          .section-content strong {
                            font-weight: 600;
                          }
                          .board-meeting {
                            background: #f8f9fa;
                            padding: 15px;
                            border-radius: 8px;
                            margin: 10px 0;
                            border-left: 4px solid ${primaryColor};
                            -webkit-print-color-adjust: exact;
                            color-adjust: exact;
                            print-color-adjust: exact;
                          }
                          .board-meeting p {
                            margin: 6px 0 !important;
                            line-height: 1.5 !important;
                          }
                          .board-meeting strong {
                            color: ${primaryColor};
                          }
                          img {
                            max-width: 100% !important;
                            height: auto !important;
                            page-break-inside: avoid;
                          }
                          .header-image {
                            width: 100%;
                            height: ${currentIssue.header_display_mode === 'full' ? 'auto' : '200px'};
                            object-fit: ${currentIssue.header_display_mode === 'full' ? 'contain' : 'cover'};
                            margin-bottom: 20px;
                            border-radius: 8px;
                          }
                          .section-image {
                            width: 100%;
                            max-width: 600px;
                            height: auto;
                            margin: 15px auto 20px auto;
                            display: block;
                            border-radius: 8px;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                          }
                          .column-content {
                            display: inline-block;
                            width: 48%;
                            vertical-align: top;
                            padding: 10px;
                            box-sizing: border-box;
                          }
                          .column-content:first-child {
                            margin-right: 2%;
                          }
                        }
                        @media screen {
                          body { 
                            font-family: 'Georgia', 'Times New Roman', serif; 
                            margin: 0; 
                            padding: 40px;
                            line-height: 1.6;
                            color: #000;
                          }
                          .header { 
                            border-bottom: 3px solid ${primaryColor}; 
                            padding-bottom: 20px; 
                            margin-bottom: 30px; 
                            text-align: center;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 15px;
                          }
                          .logo {
                            height: 60px;
                            width: 60px;
                            object-fit: contain;
                            flex-shrink: 0;
                          }
                          .title-section {
                            text-align: center;
                            flex: 1;
                          }
                          .title { 
                            font-size: 32px; 
                            font-weight: bold; 
                            margin: 0 0 10px 0; 
                            color: ${primaryColor};
                          }
                          .meta { 
                            color: #666; 
                            margin: 0;
                            font-size: 14px;
                          }
                          .content { 
                            line-height: 1.8; 
                            font-size: 16px;
                          }
                          .section-header {
                            background: linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%);
                            color: white !important;
                            padding: 12px 20px;
                            margin: 25px 0 15px 0;
                            border-radius: 6px;
                            font-size: 18px;
                            font-weight: bold;
                            text-align: center;
                          }
                          .section-content {
                            margin-bottom: 25px;
                            padding: 0 10px;
                          }
                          .section-content p {
                            margin: 8px 0;
                            text-align: justify;
                          }
                          .section-content strong {
                            font-weight: 600;
                          }
                          .board-meeting {
                            background: #f8f9fa;
                            padding: 15px;
                            border-radius: 8px;
                            margin: 10px 0;
                            border-left: 4px solid ${primaryColor};
                          }
                          .board-meeting p {
                            margin: 6px 0 !important;
                            line-height: 1.5 !important;
                          }
                          .board-meeting strong {
                            color: ${primaryColor};
                          }
                          img {
                            max-width: 100% !important;
                            height: auto !important;
                          }
                          .header-image {
                            width: 100%;
                            height: ${currentIssue.header_display_mode === 'full' ? 'auto' : '200px'};
                            object-fit: ${currentIssue.header_display_mode === 'full' ? 'contain' : 'cover'};
                            margin-bottom: 20px;
                            border-radius: 8px;
                          }
                          .section-image {
                            width: 100%;
                            max-width: 600px;
                            height: auto;
                            margin: 15px auto 20px auto;
                            display: block;
                            border-radius: 8px;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                          }
                          .column-content {
                            display: inline-block;
                            width: 48%;
                            vertical-align: top;
                            padding: 10px;
                            box-sizing: border-box;
                          }
                          .column-content:first-child {
                            margin-right: 2%;
                          }
                        }
                        @page {
                          margin: 0.75in;
                          size: letter;
                        }
                      </style>
                    </head>
                    <body>
                      ${currentIssue.header_image_id ? `
                        <img src="/api/newsletter/asset/${currentIssue.header_image_id}" alt="Newsletter Header" class="header-image" />
                      ` : ''}
                      <div class="header">
                        ${logoHtml}
                        <div class="title-section">
                          <h1 class="title">${currentIssue.title}</h1>
                          <p class="meta">
                            ${currentIssue.issue_date ? `Issue Date: ${new Date(currentIssue.issue_date).toLocaleDateString()}` : ''}
                            ${currentIssue.published_at ? ` • Published: ${new Date(currentIssue.published_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      </div>
                      <div class="content">
                        ${currentIssue.sections && currentIssue.sections.length > 0 ? 
                          currentIssue.sections
                            .filter(section => section.enabled && ((section.column_content && section.column_content.some(content => content && content.trim())) || (section.html && section.html.trim())))
                            .map(section => {
                              let sectionContent = '';
                              
                              if (section.column_content && section.column_content.length > 1) {
                                // Multi-column content using TABLE for better print support
                                const activeColumns = section.column_content.filter(content => content && content.trim());
                                const colWidth = Math.floor(100 / activeColumns.length) + '%';
                                sectionContent = `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 10px 0;"><tr>` +
                                  activeColumns.map(content => `<td width="${colWidth}" valign="top" style="padding: 0 15px;">${content}</td>`).join('') +
                                  `</tr></table>`;
                              } else {
                                // Single column content
                                const content = section.column_content?.[0] || section.html || '';
                                sectionContent = content;
                              }
                              
                              // Special formatting for board meeting sections
                              if (section.key === 'board_meeting') {
                                sectionContent = sectionContent.replace(/<p><strong>Board Meeting[^<]*<\/strong><\/p>/g, '');
                                sectionContent = `<div class="board-meeting">${sectionContent}</div>`;
                              }
                              
                              // Build section image HTML (appears AFTER header, BEFORE content)
                              const sectionImageHtml = section.header_image_id ? 
                                `<img src="/api/newsletter/asset/${section.header_image_id}" alt="${section.title} Image" class="section-image" />` : '';
                              
                              return `
                                <div class="section-header">${section.title}</div>
                                ${sectionImageHtml}
                                <div class="section-content">${sectionContent}</div>
                              `;
                            }).join('') 
                          : '<p>No content available</p>'
                        }
                      </div>
                    </body>
                  </html>
                `);
                printWindow.document.close();
                
                // Show print dialog after a brief delay to ensure content is loaded
                setTimeout(() => {
                  printWindow.print();
                }, 800);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Newsletter
            </button>
          </div>
        </div>
      </div>

      {/* Header Image */}
      {currentIssue.header_image_id && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <img
            src={`/api/newsletter/asset/${currentIssue.header_image_id}`}
            alt="Newsletter Header"
            className={`w-full rounded-lg shadow-sm ${
              currentIssue.header_display_mode === 'full' 
                ? 'h-auto' 
                : 'h-60 object-cover'
            }`}
            style={currentIssue.header_display_mode !== 'full' ? {
              objectPosition: 'center'
            } : {}}
          />
        </div>
      )}

      {/* Newsletter Content */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-8">
          <div className="prose prose-lg max-w-none">
            {currentIssue.sections && currentIssue.sections.length > 0 ? (
              currentIssue.sections
                .filter(section => section.enabled && 
                  ((section.column_content && section.column_content.some(c => c && c.trim())) || 
                   (section.html && section.html.trim())))
                .map((section, index) => (
                  <div key={section.key || index} className="mb-8 border-b border-gray-100 pb-6">
                    {/* Branded Section Header */}
                    <div 
                      className="w-full py-4 px-6 mb-6 rounded-lg shadow-sm"
                      style={{ 
                        background: `linear-gradient(135deg, ${hoaData?.branding?.primary_color || '#5A2D82'} 0%, ${hoaData?.branding?.primary_color || '#5A2D82'}dd 100%)`,
                        color: 'white'
                      }}
                    >
                      <h2 className="text-xl font-bold text-white text-center tracking-wide">
                        {section.title}
                      </h2>
                    </div>
                    
                    {/* Section Image (appears AFTER header, BEFORE content) */}
                    {section.header_image_id && (
                      <div className="mb-6 text-center">
                        <img
                          src={`/api/newsletter/asset/${section.header_image_id}`}
                          alt={`${section.title} Image`}
                          className="max-w-full h-auto object-contain rounded-lg mx-auto shadow-md"
                          style={{ maxHeight: '400px' }}
                        />
                      </div>
                    )}
                    
                    {/* Section Content with Column Layout */}
                    <div 
                      className={section.columns > 1 ? 'grid gap-5' : 'block'}
                      style={{ 
                        gridTemplateColumns: section.columns > 1 ? `repeat(${section.columns}, 1fr)` : 'none'
                      }}
                    >
                      {section.columns > 1 ? (
                        // Multi-column content
                        Array.from({ length: section.columns }, (_, colIndex) => (
                          <div key={colIndex} className="px-3">
                            {settings?.html_mode === false ? (
                              <div className="whitespace-pre-line leading-relaxed">
                                {section.column_content?.[colIndex] || ''}
                              </div>
                            ) : (
                              <div dangerouslySetInnerHTML={{ 
                                __html: section.column_content?.[colIndex] || '' 
                              }} />
                            )}
                          </div>
                        ))
                      ) : (
                        // Single column content
                        <div>
                          {settings?.html_mode === false ? (
                            <div className="whitespace-pre-line leading-relaxed">
                              {section.column_content?.[0] || section.html || ''}
                            </div>
                          ) : (
                            <div dangerouslySetInnerHTML={{ 
                              __html: section.column_content?.[0] || section.html || '' 
                            }} />
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Reaction Buttons */}
                    <ReactionButtons 
                      sectionId={`${currentIssue.id}-${section.key || index}`}
                      sectionTitle={section.title}
                      user={user}
                    />
                  </div>
                ))
            ) : (
              <p className="text-gray-500 italic">No content available for this newsletter.</p>
            )}
          </div>
        </div>
      </div>

      {/* Tags and Status */}
      {(currentIssue.tags && currentIssue.tags.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {currentIssue.tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="text-center text-sm text-gray-600">
          <p className="mb-2">
            <strong>Current Issue</strong> • This is the latest published newsletter for your community
          </p>
          <p>
            When a new newsletter is published, it will replace this issue and move the current one to The Vault.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewsletterDashboard;